/**
 * @file        src/pages/site/SiteSetting.jsx
 * @description Site Setting (Site Management). Single-row upsert of the company
 *              address + contact + CC email, plus the per-brand Storefront
 *              Branding cards. All data is live from `site_settings` / `brands`
 *              (no static content).
 * @author      Kinetika Labs
 * @created     2026-07-04
 * @changelog
 *   - 2026-09-16 Claude — Main Page Heading List panel removed (migration 062).
 *                          The rows were admin-editable but unreachable: the whole
 *                          /api/site-settings router sits behind authenticate +
 *                          requireModule('Site Management'), booking_backend never
 *                          queried `site_headings`, and neither storefront home
 *                          page referenced it — both render their own copy. Same
 *                          call as Home Slider Image (060): a CMS screen whose rows
 *                          no storefront can fetch is scaffolding, not a feature.
 *   - 2026-09-16 Claude — Phone is EXACTLY 10 digits, numbers only, and non-digits
 *                          are stripped as typed. It was `optStr(60)` on both
 *                          sides, so "987654yyyyyyy###3" saved and printed on
 *                          invoices, vouchers and e-tickets as the number a
 *                          customer is meant to call. The placeholder no longer
 *                          reads "+91 …" — it was inviting a format the field now
 *                          rejects. A pasted country code is NOT truncated away;
 *                          it shows the error so the admin removes it themselves,
 *                          because silently keeping the wrong 10 of 12 digits is
 *                          worse than the error. Mirrors siteSettings.routes.js.
 *                          Company Name and Address are deliberately left as
 *                          length-bounded free text: real names and addresses
 *                          carry & . , - / and restricting them would reject
 *                          valid input.
 *   - 2026-07-04 Claude — Initial (Site Management → Site Setting).
 *   - 2026-07-23 Claude — Removed the SEO metadata fields (meta title/description/
 *                          keyword); Address is now a mandatory field and the
 *                          single source of truth reused across all documents,
 *                          emails and the website.
 *   - 2026-08-04 Claude — Logo/favicon fields enforce the shared brandingSpecs
 *                          size rules (same limits as the super admin's
 *                          onboarding panel) — an off-size upload is rejected
 *                          before it reaches the server, with the accepted
 *                          range spelled out under each field.
 *   - 2026-08-04 Claude — Enquiry notification emails field (CR A5): admin-
 *                          configurable recipient list for the "Send us a
 *                          Message" contact form; falls back to Email/CC
 *                          Email above when left blank.
 *   - 2026-08-10 Claude — Logo & Favicon section removed and replaced with
 *                          "Storefront Branding" (per active brand). Those two
 *                          fields used to write site_settings.logo_url/
 *                          favicon_url, which ALSO fed the admin console's own
 *                          chrome (Sidebar, Login, BrandingHead) — so setting
 *                          the "admin portal logo" here silently changed the
 *                          customer booking site's logo too. The admin portal's
 *                          branding is now super_admin-only (Platform → Tenant
 *                          Management → Admin Portal Branding); this page owns
 *                          only the BOOKING site's per-brand logo/favicon via
 *                          PATCH /api/brands/:id/branding.
 *   - 2026-08-10 Claude — "Issuing company" section (migration 051): company
 *                          name, registered legal name, support email, website
 *                          and legal jurisdiction. These were frontend
 *                          constants (lib/company.js), so EVERY tenant printed
 *                          tenant 1's company on its invoices, e-tickets and
 *                          vouchers — the address was the only tenant-owned
 *                          field. This section is where a tenant sets its own.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import ImageUpload from '../../components/ImageUpload';
import { get, put, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { LOGO_CHECK, LOGO_HINT, FAVICON_CHECK, FAVICON_HINT } from '../../lib/brandingSpecs';

const optStr = (max) => z.string().trim().max(max).optional().or(z.literal(''));
const schema = z.object({
  // The issuing company printed on every document. Left optional so the Social
  // Links form (same endpoint) still saves, but the UI marks it clearly: an
  // unset company prints '—' on the invoice rather than another tenant's name.
  company_name: optStr(190),
  company_legal_name: optStr(190),
  support_email: z.union([z.string().trim().email('Invalid email').max(190), z.literal('')]).optional(),
  website: optStr(255),
  jurisdiction: optStr(190),
  // Address is the single source of truth reused across tickets, vouchers,
  // invoices, emails and the website — it is mandatory here.
  address: z.string().trim().min(1, 'Address is required').max(500),
  city: optStr(120), state: optStr(120), country: optStr(120),
  pincode: optStr(20),
  // EXACTLY 10 digits. This is the number printed on every invoice, voucher and
  // e-ticket, so a customer dials it — `optStr(60)` accepted "987654yyyyyyy###3".
  // 10 is a mobile, or a landline as STD code + number with NO trunk prefix
  // (22 26123456, not 022 26123456 — the leading 0 makes it 11 and is rejected).
  // A country code is not stored: "+91" is presentation, and allowing "+" is what
  // made this a free-text field in the first place.
  phone: z.union([
    z.string().trim().regex(/^[0-9]{10}$/, 'Enter exactly 10 digits, numbers only'),
    z.literal(''),
  ]).optional(),
  email: z.union([z.string().trim().email('Invalid email').max(190), z.literal('')]).optional(),
  news: optStr(255), cc_email: optStr(1000),
  enquiry_notify_emails: optStr(500),
});

const empty = {
  company_name: '', company_legal_name: '', support_email: '', website: '', jurisdiction: '',
  address: '', city: '', state: '', country: '', pincode: '', phone: '', email: '', news: '',
  cc_email: '', enquiry_notify_emails: '',
};

export default function SiteSetting() {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data } = useQuery({ queryKey: qk.siteSettings.get, queryFn: () => get('/site-settings') });

  useEffect(() => {
    const t = data?.data;
    if (t && Object.keys(t).length) {
      setForm((prev) => ({ ...prev, ...Object.fromEntries(Object.keys(empty).map((k) => [k, t[k] || ''])) }));
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: (body) => put('/site-settings', body),
    onSuccess: () => {
      toast.ok('Saved', 'Site settings updated.');
      qc.invalidateQueries({ queryKey: qk.siteSettings.get });
      // The documents read the issuer from its own cached endpoint — refresh it
      // too, or a just-corrected company keeps printing the old details.
      qc.invalidateQueries({ queryKey: qk.siteSettings.company });
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    setErrors({});
    mut.mutate(parsed.data);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHead title="Site Setting" subtitle="Site Management" />

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <SectionTitle>Issuing company</SectionTitle>
          <div className="field full">
            <div className="field-hint muted">
              Printed as the issuer on every tax invoice, e-ticket and hotel voucher, and used to
              sign the emails that send them. Leave a field blank and the document prints “—” — it
              never falls back to another company.
            </div>
          </div>
          <Field label="Company Name" err={errors.company_name}>
            <input value={form.company_name} onChange={on('company_name')} placeholder="Trading name, e.g. Beyond Destination" />
          </Field>
          <Field label="Registered Legal Name" err={errors.company_legal_name}>
            <input value={form.company_legal_name} onChange={on('company_legal_name')} placeholder="e.g. Beyond Destination Pvt. Ltd." />
            <div className="field-hint muted">The registered entity shown in the invoice “From” block.</div>
          </Field>
          <Field label="Support Email" err={errors.support_email}>
            <input value={form.support_email} onChange={on('support_email')} placeholder="support@…" />
          </Field>
          <Field label="Website" err={errors.website}>
            <input value={form.website} onChange={on('website')} placeholder="www.example.com" />
          </Field>
          <Field label="Legal Jurisdiction" err={errors.jurisdiction}>
            <input value={form.jurisdiction} onChange={on('jurisdiction')} placeholder="e.g. Mumbai, India" />
            <div className="field-hint muted">Named in the invoice terms (“disputes subject to … jurisdiction”).</div>
          </Field>

          <SectionTitle>Address</SectionTitle>
          <Field label="Address" err={errors.address} required full>
            <textarea rows={2} value={form.address} onChange={on('address')} placeholder="Enter address" />
            <div className="field-hint muted">Used across tickets, vouchers, invoices, emails and the website.</div>
          </Field>
          <Field label="City" err={errors.city}><input value={form.city} onChange={on('city')} placeholder="City" /></Field>
          <Field label="State" err={errors.state}><input value={form.state} onChange={on('state')} placeholder="State" /></Field>
          <Field label="Country" err={errors.country}><input value={form.country} onChange={on('country')} placeholder="Country" /></Field>
          <Field label="Pincode" err={errors.pincode}><input value={form.pincode} onChange={on('pincode')} placeholder="Pincode" /></Field>

          <SectionTitle>Contact &amp; news</SectionTitle>
          {/* Digits stripped as typed, and NOT truncated: a pasted "+91 98765 43210"
              becomes 12 digits and shows the error, rather than being silently cut
              to the wrong 10. The admin deletes the 91 themselves. */}
          <Field label="Phone" err={errors.phone}>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, '') })}
              inputMode="numeric"
              placeholder="10-digit number, e.g. 9876543210"
            />
            <div className="field-hint">Exactly 10 digits, numbers only — no country code, spaces or symbols.</div>
          </Field>
          <Field label="Email" err={errors.email}><input value={form.email} onChange={on('email')} placeholder="bookings@…" /></Field>
          <Field label="News" err={errors.news}><input value={form.news} onChange={on('news')} placeholder="News headline" /></Field>
          <Field label="CC Email" err={errors.cc_email} full>
            <input value={form.cc_email} onChange={on('cc_email')} placeholder="Comma-separated, no spaces" />
            <div className="field-hint muted">If multiple emails, enter without spaces and separated by comma.</div>
          </Field>
          <Field label="Enquiry notification emails (comma-separated)" err={errors.enquiry_notify_emails} full>
            <input
              value={form.enquiry_notify_emails}
              onChange={on('enquiry_notify_emails')}
              placeholder="e.g. sales@example.com,leads@example.com"
            />
            <div className="field-hint muted">
              Who gets notified when a customer submits the "Send us a Message" contact form or a package
              enquiry. Leave blank to keep using Email / CC Email above.
            </div>
          </Field>

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </form>
      </article>

      <StorefrontBranding />
    </>
  );
}

function SectionTitle({ children }) {
  return <div className="field full"><h4 className="form-section">{children}</h4></div>;
}

function Field({ label, err, full, required, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}

// --- Storefront Branding ---------------------------------------------------
// One card per ACTIVE brand — the booking site's own logo/favicon (never the
// admin console's, which a super admin alone controls under Platform → Tenant
// Management → Admin Portal Branding). A tenant running two storefronts (e.g.
// Neon + Beyond) edits each one's look independently here.
function StorefrontBranding() {
  const { data, isLoading, isError } = useQuery({
    queryKey: qk.brands.list,
    queryFn: () => get('/brands').then((r) => r.data),
  });
  const brands = data || [];

  return (
    <article className="panel glass" style={{ marginTop: 16 }}>
      <div className="panel-head">
        <div>
          <h3 className="panel-title">Storefront Branding</h3>
          <p className="muted">
            The logo and favicon shown on the customer booking site — one per active brand. The
            admin console's own logo/favicon are set by a super admin, not here.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="empty">Loading brands…</div>
      ) : isError ? (
        <div className="empty"><p className="field-error">Could not load brands.</p></div>
      ) : brands.length === 0 ? (
        <div className="empty">No active brand configured for this tenant.</div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {brands.map((b) => <BrandBrandingRow key={b.id} brand={b} />)}
        </div>
      )}
    </article>
  );
}

function BrandBrandingRow({ brand }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ logo_url: brand.logo_url || '', favicon_url: brand.favicon_url || '' });

  const mut = useMutation({
    mutationFn: (body) => patch(`/brands/${brand.id}/branding`, body),
    onSuccess: () => {
      toast.ok('Saved', `${brand.name} branding updated.`);
      qc.invalidateQueries({ queryKey: qk.brands.list });
    },
    onError: (err) => toast.err('Could not save', err.response?.data?.error?.message || 'Failed'),
  });

  return (
    <form
      className="form-grid"
      onSubmit={(e) => { e.preventDefault(); mut.mutate(form); }}
      style={{ borderTop: '1px solid var(--border, rgba(255,255,255,.08))', paddingTop: 14 }}
    >
      <div className="field full">
        <label className="section-label">{brand.name} ({brand.code})</label>
      </div>
      <Field label="Logo">
        <ImageUpload value={form.logo_url} onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} check={LOGO_CHECK} />
        <div className="field-hint muted">{LOGO_HINT}</div>
      </Field>
      <Field label="Favicon">
        <ImageUpload value={form.favicon_url} onChange={(v) => setForm((f) => ({ ...f, favicon_url: v }))} check={FAVICON_CHECK} />
        <div className="field-hint muted">{FAVICON_HINT}</div>
      </Field>
      <div className="form-actions">
        <button className="btn primary sm" type="submit" disabled={mut.isPending}>
          {mut.isPending ? 'Saving…' : `Save ${brand.name} Branding`}
        </button>
      </div>
    </form>
  );
}
